import React, { useState, useEffect } from 'react';
import { formatDateForInput } from '../../utils/dateUtils';

const EmployeeStatement = () => {
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [userId, setUserId] = useState('');
    const [employeesData, setEmployeesData] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});

    const apiUrl = import.meta.env.VITE_API_URL;
    const getTotalOrdersPerUserEndpoint = import.meta.env.VITE_get_total_orders_per_user;

    useEffect(() => {
        // Set default dates to today
        const today = formatDateForInput(new Date());
        setFromDate(today);
        setToDate(today);
    }, []);

    const fetchEmployeesData = async () => {
        if (!fromDate || !toDate) return;

        try {
            setLoading(true);
            setValidationErrors({});
            
            const params = new URLSearchParams();
            params.append('fromDate', fromDate);
            params.append('endDate', toDate);
            if (userId.trim()) {
                params.append('UserId', userId.trim());
            }

            const response = await fetch(`${apiUrl}${getTotalOrdersPerUserEndpoint}?${params}`);

            if (response.ok) {
                const data = await response.json();
                setEmployeesData(data || []);
            } else {
                console.error('Error fetching employees data');
                setValidationErrors({ submit: 'حدث خطأ أثناء جلب البيانات' });
            }
        } catch (error) {
            console.error('Error fetching employees data:', error);
            setValidationErrors({ submit: 'حدث خطأ أثناء جلب البيانات' });
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        // Clear previous validation errors
        setValidationErrors({});
        
        // Validate dates
        if (!fromDate) {
            setValidationErrors(prev => ({ ...prev, fromDate: 'يرجى اختيار تاريخ البداية' }));
            return;
        }
        if (!toDate) {
            setValidationErrors(prev => ({ ...prev, toDate: 'يرجى اختيار تاريخ النهاية' }));
            return;
        }
        if (new Date(fromDate) > new Date(toDate)) {
            setValidationErrors(prev => ({ ...prev, toDate: 'تاريخ النهاية يجب أن يكون بعد تاريخ البداية' }));
            return;
        }
        
        fetchEmployeesData();
    };

    const downloadExcel = () => {
        if (employeesData.length === 0) return;
        
        // Create CSV content
        let csvContent = '\uFEFF'; // UTF-8 BOM for Arabic
        csvContent += 'التاريخ,من,إلى,رقم الموظف,إسم الموظف,إجمالي الطلبات\n';
        
        employeesData.forEach((emp, index) => {
            csvContent += `"${fromDate}","${fromDate}","${toDate}","${emp.nagwaId}","${emp.name}","${emp.total}"\n`;
        });
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `كشف_طلبات_الموظفين_${fromDate}_${toDate}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <section className="main_content">
            <style>
                {`
                    .employee-statement-table td {
                        text-align: right !important;
                    }
                    .employee-statement-table th {
                        text-align: right !important;
                    }
                    .employee-statement-table bdo {
                        text-align: right !important;
                    }
                    input[type="date"] {
                        text-align: right !important;
                        direction: rtl !important;
                    }
                    input[type="date"]:hover {
                        border-color: #f6a500 !important;
                        box-shadow: 0 0 0 0.2rem rgba(246, 165, 0, 0.25) !important;
                    }
                    input[type="date"]:focus {
                        border-color: #f6a500 !important;
                        box-shadow: 0 0 0 0.2rem rgba(246, 165, 0, 0.25) !important;
                    }
                    input[type="text"] {
                        text-align: right !important;
                        direction: rtl !important;
                    }
                    input[type="text"]:hover {
                        border-color: #f6a500 !important;
                        box-shadow: 0 0 0 0.2rem rgba(246, 165, 0, 0.25) !important;
                    }
                    input[type="text"]:focus {
                        border-color: #f6a500 !important;
                        box-shadow: 0 0 0 0.2rem rgba(246, 165, 0, 0.25) !important;
                    }
                `}
            </style>
            <div className="container">
                <h1 className="page-title">كشف طلبات الموظفين</h1>

                {/* Export Button */}
                {employeesData.length > 0 && (
                    <div className="sheet_actions">
                        <button 
                            type="button" 
                            className="btn_icon" 
                            onClick={downloadExcel}
                            title="تنزيل الملف"
                        >
                            <i className="mIcon">&#xf220;</i>
                        </button>
                    </div>
                )}

                {/* Form */}
                <form className="form-horizontal" onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
                    <div className="form-group">
                        <label>من:</label>
                        <div className="field">
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => {
                                    setFromDate(e.target.value);
                                    if (validationErrors.fromDate) {
                                        setValidationErrors(prev => ({ ...prev, fromDate: '' }));
                                    }
                                }}
                                className={`form-control ${validationErrors.fromDate ? 'error' : ''}`}
                            />
                            {validationErrors.fromDate && (
                                <div className="alert danger">
                                    {validationErrors.fromDate}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label>إلى:</label>
                        <div className="field">
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => {
                                    setToDate(e.target.value);
                                    if (validationErrors.toDate) {
                                        setValidationErrors(prev => ({ ...prev, toDate: '' }));
                                    }
                                }}
                                className={`form-control ${validationErrors.toDate ? 'error' : ''}`}
                            />
                            {validationErrors.toDate && (
                                <div className="alert danger">
                                    {validationErrors.toDate}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label>رقم الموظف:</label>
                        <div className="field">
                            <input
                                type="text"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value)}
                                className="form-control"
                                placeholder="أدخل رقم الموظف (اختياري)"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label></label>
                        <div className="field">
                            <button type="submit" className="btn btn-primary">
                                عرض
                            </button>
                        </div>
                    </div>
                </form>

                {/* Error Message */}
                {validationErrors.submit && (
                    <div className="alert danger">
                        {validationErrors.submit}
                    </div>
                )}

                {/* Loading */}
                {loading && (
                    <div className="loading-message">جاري التحميل...</div>
                )}

                {/* Results */}
                {employeesData.length > 0 && (
                    <div className="food_list">
                        <table className="table table-bordered table-curved employee-statement-table">
                            <thead className="thead-inverse">
                                <tr>
                                    <th>إسم الموظف</th>
                                    <th>رقم الموظف</th>
                                    <th>إجمالى الطلبات</th>
                                </tr>
                            </thead>
                            <tbody>
                                {employeesData.map((emp, index) => (
                                    <tr key={index}>
                                        <td>{emp.name}</td>
                                        <td>
                                            <bdo dir="ltr">{emp.nagwaId}</bdo>
                                        </td>
                                        <td>
                                            <bdo dir="ltr">{emp.total}</bdo>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* No Data Message */}
                {employeesData.length === 0 && !loading && fromDate && toDate && (
                    <div className="no-data">لا توجد بيانات للعرض</div>
                )}
            </div>
        </section>
    );
};

export default EmployeeStatement;
